import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { X, Check, Edit, ArrowRight } from 'lucide-react';

const ComparisonModal = ({ 
  original, 
  enhanced, 
  onUseOriginal, 
  onUseEnhanced, 
  onEditEnhanced,
  onClose 
}) => {

  // Lock body scroll when modal is open
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
      document.body.style.top = '';
    };
  }, []);

  const modalContent = (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        margin: 0
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-dark-bg rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col"
        style={{ 
          maxWidth: '80rem',
          maxHeight: '90vh',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-text-dark dark:text-dark-text">
              AI Enhancement Preview
            </h3>
            <p className="text-sm text-text-gray dark:text-dark-text-secondary mt-1">
              Compare and choose which version to use
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-gray hover:text-text-dark dark:hover:text-dark-text transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Comparison */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Original */}
            <div>
              <div className="flex items-center mb-3">
                <h4 className="font-semibold text-text-dark dark:text-dark-text">
                  Your Original
                </h4>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-xl border border-gray-200 dark:border-dark-border min-h-[120px]">
                <p className="text-text-dark dark:text-dark-text whitespace-pre-wrap">
                  {original}
                </p>
              </div>
            </div>

            {/* Enhanced */}
            <div>
              <div className="flex items-center mb-3">
                <h4 className="font-semibold text-text-dark dark:text-dark-text flex items-center gap-2">
                  AI Enhanced (Structured)
                  <ArrowRight className="h-4 w-4 text-primary" />
                </h4>
              </div>
              <div className="p-4 bg-gradient-to-br from-primary/5 to-primary-dark/5 rounded-xl border-2 border-primary/30 min-h-[120px]">
                {enhanced.includes('**Primary Function:**') ? (
                  <div className="space-y-3">
                    {enhanced.split(/\n\n/).map((section, idx) => {
                      if (!section.trim()) return null;
                      const headerMatch = section.match(/\*\*(.+?):\*\*/);
                      if (headerMatch) {
                        const header = headerMatch[1];
                        const content = section.replace(/\*\*.+?\*\*/, '').trim();
                        return (
                          <div key={idx}>
                            <div className="text-sm font-bold text-primary mb-1">
                              {header}:
                            </div>
                            <div className="text-text-dark dark:text-dark-text text-sm">
                              {content}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                ) : (
                  <p className="text-text-dark dark:text-dark-text whitespace-pre-wrap font-medium">
                    {enhanced}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Quality Improvements */}
          <div className="mt-6 p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl">
            <h5 className="font-bold text-green-900 dark:text-green-200 mb-3 flex items-center gap-2">
              <span className="text-xl">✨</span>
              Strategic Improvements Made
            </h5>
            <ul className="text-sm text-green-900 dark:text-green-200 space-y-2.5">
              {enhanced.includes('**Primary Function:**') && !original.includes('**Primary Function:**') && (
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                  <span><strong>Structured for clarity</strong> - Organized into Primary Function, Ideal Capabilities, and Key Differentiators for maximum comprehension</span>
                </li>
              )}
              {enhanced.toLowerCase().includes('value') || enhanced.toLowerCase().includes('unique') || enhanced.toLowerCase().includes('differentiator') ? (
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                  <span><strong>Articulated unique value</strong> - Clarified what makes this skill distinct and valuable in the ecosystem</span>
                </li>
              ) : null}
              {(enhanced.match(/\b(Generate|Create|Analyze|Build|Craft|Produce|Design|Transform|Enable|Provide)\b/gi) || []).length > 
               (original.match(/\b(Generate|Create|Analyze|Build|Craft|Produce|Design|Transform|Enable|Provide)\b/gi) || []).length && (
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                  <span><strong>Defined precise capabilities</strong> - Used specific action verbs to clarify exactly what the skill does</span>
                </li>
              )}
              {(enhanced.match(/\b(specifically|for example|such as|including|like|enables|allows)\b/gi) || []).length > 
               (original.match(/\b(specifically|for example|such as|including|like|enables|allows)\b/gi) || []).length && (
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                  <span><strong>Added concrete examples</strong> - Provided specific use cases and outputs for better context</span>
                </li>
              )}
              {enhanced.split(/[.!?]/).filter(s => s.trim()).length > original.split(/[.!?]/).filter(s => s.trim()).length && (
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                  <span><strong>Enhanced readability</strong> - Broke complex ideas into clear, digestible statements</span>
                </li>
              )}
              {enhanced.length > original.length + 50 && (
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                  <span><strong>Expanded strategic context</strong> - Added depth to help the AI generator understand the full scope and intent</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-secondary">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onUseOriginal}
              className="rounded-xl"
            >
              Keep Original
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onEditEnhanced}
              className="rounded-xl border-primary text-primary hover:bg-primary hover:text-white"
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit Enhanced
            </Button>
            <Button
              type="button"
              onClick={onUseEnhanced}
              className="gradient-primary text-white rounded-xl"
            >
              <Check className="mr-2 h-4 w-4" />
              Use Enhanced
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default ComparisonModal;
